/**
 * The Agents screen, both directions.
 *
 * "Agents" is ambiguous in a gallery and the copy resolves it in words rather
 * than leaving the reader to infer it from a list: a BUYER agent acts for a
 * collector and the gallery sends them works, a SELLER agent acts for a vendor
 * and brings works in. Both are the same relation read from opposite ends —
 * `ondo.work_agent_mandates` — which is why there is one screen and not two.
 */
export const GALLERY_AGENTS_COPY = {
  loading: "Loading your agents…",
  failed: "Could not load your agents. Try again in a moment.",
  forbidden: "This account holds no gallery seat, so it has no agents to show.",

  /**
   * The standing explainer, in the same green panel Inventory opens with.
   *
   * It says access is granted PER WORK and that the ONDO code travels with it,
   * because both are true and both are the things a dealer most needs to know.
   * It deliberately does NOT promise permissions the gallery chooses: what a
   * mandate carries is fixed in SQL, identically for everyone.
   */
  lead: "The gallery works with agents on both sides of a sale.",
  leadDetail:
    "A buyer agent acts for a collector — you send them a work from your inventory to place with their client. A seller agent acts for a vendor — they bring works to you. Access is granted one work at a time, and every work an agent touches keeps its ONDO code, so its provenance travels with it.",

  /** The collapsed panel under the banner. */
  primerTitle: "How agent representation works, and what a mandate carries",
  primerSubtitle: "The buyer-agent flow, the seller-agent flow, and the access each one grants",
  primerShow: "Show",
  primerHide: "Hide",
  primer: [
    {
      key: "buyer",
      heading: "Sending a work to a buyer agent",
      body: "Open the work's record and appoint the agent by their ONDO id. You may do this for any work you hold on a live consignment and could sell — if the registry would let you sell it, it will let you appoint somebody to show it. The mandate ends when you end it, and it ends by itself when the consignment does, so handing a picture back never leaves an agent inside its record.",
    },
    {
      key: "seller",
      heading: "A seller agent bringing you a work",
      body: "An agent acting for a vendor offers you the work the way an owner does, and it arrives in Inventory under “Offered to you”. Accepting puts it in your inventory; declining ends the offer and tells them. The owner is notified either way — it is their picture, and the offer was made on their behalf.",
    },
    {
      key: "carries",
      heading: "What a mandate carries, and what it does not",
      body: "The same access whoever granted it: the agent may read the work's full record and correct its catalogue entry. They may not sell it, share it on, appoint an agent of their own, or learn anything about the collection holding it. There is nothing to configure here — a grant whose powers varied per agent would be this screen asserting an authorisation, and authorisation is the database's.",
    },
  ],

  /** The three counts. */
  tileBuyers: "Buyer agents",
  tileBuyersNote: ["work out on placement", "works out on placement"] as const,
  tileOffers: "Seller offers open",
  tileOffersNote: "Awaiting your decision",
  tileSellers: "Seller agents",
  tileSellersNote: "Bringing works to the gallery",

  /**
   * The two sides, as CHIPS rather than tabs.
   *
   * Every other screen in this product switches between subsets with `ChipRow` —
   * Inventory by artist, the agent's list by artist, the collection grid by
   * status. A tab bar here would be a second navigation idiom for the same job.
   */
  buyerChip: "Buyer agents",
  sellerChip: "Seller agents",
  sideLabel: "Side",

  /** On an agent's card. */
  firm: "Advisory",
  person: "Agent",
  /**
   * NOT "KYC verified", which is what this said and is the mislabel OND-75 calls
   * "the single most dangerous" in the product.
   *
   * The flag behind it is `ondo.principals.verified`, a boolean ONDO sets by
   * hand. `packages/shared/src/api/agents.ts` has always described it as "ONDO's
   * word about them, not their own", and `profile.constants.ts` says outright
   * that there is "no KYC table, no tier, no document store and no provider".
   * Nothing checks an email, let alone a passport.
   *
   * So the words say what it is. A gallery's compliance officer reading "KYC
   * verified" beside an advisory firm would reasonably conclude ONDO had seen
   * their documents and rely on it; reading "Attested by ONDO" they learn the
   * true and much smaller thing — that ONDO vouches for this firm existing and
   * being who it says.
   */
  verified: "Attested by ONDO",
  worksSent: "Works sent to this agent",
  view: "View →",
  sent: "Sent",
  recall: "Recall",
  collapse: "Collapse",
  expand: "Expand",

  /** On a seller's card. */
  offering: "Offering",
  offered: "Offered to you",
  answerAt: "Answer these in Inventory, where the terms are.",

  noBuyers:
    "You have not sent any works out to a buyer agent. Open a work in your inventory and appoint one from its record.",
  noSellers:
    "Nobody has offered you a work. When an owner or their agent consigns one to you, they appear here and in Inventory.",
} as const;
